import type { ContactType } from '../../../contacts/types/contactType'
import { EInvoiceIneligibilityReason } from '../../enums/eInvoiceIneligibilityReason'
import type { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import type { InvoiceStatus } from '../../enums/invoiceStatus'
import type { QuoteStatus } from '../../enums/quoteStatus'
import type { ZervantContactType } from '../../enums/zervantContactType'
import { isDocumentTypeCreditNote } from '../isDocumentTypeCreditNote'
import { isDocumentTypeInvoice } from '../isDocumentTypeInvoice'
import { getEInvoiceIneligibilityReasons } from './getEInvoiceIneligibilityReasons'
import { isDocumentEligibleForEInvoice } from './isDocumentEligibleForEInvoice'
import { isEligibleEInvoiceStatus } from './isEligibleEInvoiceStatus'

interface GetDocumentEInvoiceEligibilityOptions {
  contactCountry?: string | null
  contactType?: ZervantContactType | ContactType
  documentStatus?: InvoiceStatus | QuoteStatus
  documentType: InvoiceDocumentType
  isCustomerPublicEntity?: boolean
  isFrenchComplianceMode: boolean
  isPdpIntegrationActive: boolean
}

interface DocumentEInvoiceEligibility {
  ineligibilityReasons: EInvoiceIneligibilityReason[]
  isEligibleForEInvoice: boolean
  shouldShowSendAsEInvoiceOption: boolean
}

export const getDocumentEInvoiceEligibility = (
  options: GetDocumentEInvoiceEligibilityOptions,
): DocumentEInvoiceEligibility => {
  const {
    contactCountry,
    contactType,
    documentStatus,
    documentType,
    isCustomerPublicEntity,
    isFrenchComplianceMode,
    isPdpIntegrationActive,
  } = options

  const isEligibleDocument =
    (isDocumentTypeInvoice(documentType) || isDocumentTypeCreditNote(documentType)) &&
    isEligibleEInvoiceStatus(documentStatus, documentType)

  if (!isFrenchComplianceMode) {
    const shouldShowSendAsEInvoiceOption = isDocumentEligibleForEInvoice({
      contactCountry,
      contactType,
      documentStatus,
      documentType,
      isCustomerPublicEntity,
      isOrganizationFrench: false,
    })

    return {
      ineligibilityReasons: [],
      isEligibleForEInvoice: shouldShowSendAsEInvoiceOption,
      shouldShowSendAsEInvoiceOption,
    }
  }

  // Client-shape reasons are only meaningful once the organization's own registration is
  // approved and the document itself is eligible. Otherwise the option is hidden outright.
  const shouldShowSendAsEInvoiceOption = isPdpIntegrationActive && isEligibleDocument
  const ineligibilityReasons = shouldShowSendAsEInvoiceOption
    ? getEInvoiceIneligibilityReasons({ contactCountry, contactType, isCustomerPublicEntity })
    : []

  return {
    ineligibilityReasons,
    isEligibleForEInvoice: shouldShowSendAsEInvoiceOption && ineligibilityReasons.length === 0,
    shouldShowSendAsEInvoiceOption,
  }
}
